import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import DataGrid, { Column, Editing, Lookup, RequiredRule, Button as GridButton, Toolbar, Item, SearchPanel, Form, Paging, Pager, Scrolling } from 'devextreme-react/data-grid';
import { useApiMicrobiology } from '../../../api/api.microbiology';
import { Item as ItemF } from 'devextreme-react/form';
import { SysI_MicrobiologiaAntibioticosCatalogo } from '../../../types/types.microbiology';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { newToast } from '../../../components';
import { Button, Popup, Tooltip } from 'devextreme-react';

// Familias de antibióticos comunes
const familiaOptions = [
  { value: 'Penicilinas', text: 'Penicilinas' },
  { value: 'Cefalosporinas', text: 'Cefalosporinas' },
  { value: 'Carbapenémicos', text: 'Carbapenémicos' },
  { value: 'Aminoglucósidos', text: 'Aminoglucósidos' },
  { value: 'Macrólidos', text: 'Macrólidos' },
  { value: 'Fluoroquinolonas', text: 'Fluoroquinolonas' },
  { value: 'Tetraciclinas', text: 'Tetraciclinas' },
  { value: 'Glicopéptidos', text: 'Glicopéptidos' },
  { value: 'Sulfonamidas', text: 'Sulfonamidas' },
  { value: 'Oxazolidinonas', text: 'Oxazolidinonas' },
  { value: 'Otros', text: 'Otros' },
];

// Generaciones (principalmente para Cefalosporinas)
const generacionOptions = [
  { value: '1era', text: '1era Generación' },
  { value: '2da', text: '2da Generación' },
  { value: '3era', text: '3era Generación' },
  { value: '4ta', text: '4ta Generación' },
  { value: '5ta', text: '5ta Generación' },
  { value: 'N/A', text: 'N/A' },
];

interface AntibioticsCatalogProps {
  dataAntibioticos: SysI_MicrobiologiaAntibioticosCatalogo[];
  update: () => void;
}

export const AntibioticsCatalog: React.FC<AntibioticsCatalogProps> = ({ dataAntibioticos, update }) => {
  const dataGridRef = useRef<DataGrid>(null);
  const { createAntibiotico, updateAntibiotico, deleteAntibiotico, validarEliminacionAntibiotico } = useApiMicrobiology();

  const handleRefresh = useCallback(() => {
    update();
  }, [update]);

  const handleDuplicate = useCallback((e: any) => {
    const rowData = e.row.data;
    if (!rowData) return;

    dataGridRef.current?.instance.addRow();

    setTimeout(() => {
      const visibleRows = dataGridRef.current?.instance.getVisibleRows();
      const newRowIndex = visibleRows?.findIndex((row: any) => row.isNewRow);

      if (newRowIndex !== -1 && newRowIndex !== undefined) {
        dataGridRef.current?.instance.cellValue(newRowIndex, 'Abc_Nombre', `${rowData.Abc_Nombre} - Copia`);
        dataGridRef.current?.instance.cellValue(newRowIndex, 'Abc_Codigo', rowData.Abc_Codigo);
        dataGridRef.current?.instance.cellValue(newRowIndex, 'Abc_FamiliaAntibiotico', rowData.Abc_FamiliaAntibiotico);
        dataGridRef.current?.instance.cellValue(newRowIndex, 'Abc_SubFamilia', rowData.Abc_SubFamilia);
        dataGridRef.current?.instance.cellValue(newRowIndex, 'Abc_Generacion', rowData.Abc_Generacion);
        dataGridRef.current?.instance.cellValue(newRowIndex, 'Abc_OrdenPresentacion', rowData.Abc_OrdenPresentacion);
        dataGridRef.current?.instance.cellValue(newRowIndex, 'Abc_Estado', true);
      }
    }, 100);

    newToast('INFO', 'Antibiótico duplicado. Complete la edición para guardar.', 3);
  }, []);

  const customDataStore = useMemo(() => {
    return new CustomStore({
      key: 'Abc_Id',
      load: async () => {
        return {
          data: dataAntibioticos || [],
          totalCount: dataAntibioticos?.length || 0,
        };
      },
      insert: async (values) => {
        try {
          const newAntibiotico: SysI_MicrobiologiaAntibioticosCatalogo = {
            Abc_Id: 0,
            Abc_Nombre: values.Abc_Nombre,
            Abc_Codigo: values.Abc_Codigo || '',
            Abc_FamiliaAntibiotico: values.Abc_FamiliaAntibiotico,
            Abc_SubFamilia: values.Abc_SubFamilia || '',
            Abc_Generacion: values.Abc_Generacion || '',
            Abc_OrdenPresentacion: values.Abc_OrdenPresentacion || 100,
            Abc_Estado: true,
            Abc_FechaCreacion: new Date(),
          };

          await createAntibiotico(newAntibiotico);
          handleRefresh();
          return newAntibiotico;
        } catch (error) {
          throw error;
        }
      },
      update: async (key, values) => {
        try {
          const currentData = dataGridRef.current?.instance.getDataSource().items();
          const currentItem = currentData?.find((item: any) => item.Abc_Id === key);

          const updatedAntibiotico: SysI_MicrobiologiaAntibioticosCatalogo = {
            ...currentItem,
            ...values,
          };

          await updateAntibiotico(updatedAntibiotico);
          handleRefresh();
        } catch (error) {
          throw error;
        }
      },
      remove: async (key) => {
        try {
          // Validar antes de eliminar
          const validation = await validarEliminacionAntibiotico(key);

          if (validation && !validation.CanDelete) {
            newToast('WARNING', validation.Message, 5);
            throw new Error(validation.Message);
          }

          await deleteAntibiotico(key);
          handleRefresh();
        } catch (error) {
          throw error;
        }
      },
    });
  }, [dataAntibioticos, handleRefresh]);

  const dataSource = useMemo(() => {
    return new DataSource({
      store: customDataStore,
      reshapeOnPush: true,
    });
  }, [customDataStore]);

  const onEditorPreparing = (e: any) => {
    if (e.dataField === 'Abc_Estado' && e.parentType === 'dataRow') {
      e.editorName = 'dxCheckBox';
    }
  };

  const familyRender = (data: any) => {
    const familyColors: { [key: string]: string } = {
      Penicilinas: '#e3f2fd',
      Cefalosporinas: '#f3e5f5',
      Carbapenémicos: '#e8f5e9',
      Aminoglucósidos: '#fff3e0',
      Macrólidos: '#fce4ec',
      Fluoroquinolonas: '#e0f2f1',
      Tetraciclinas: '#fff8e1',
      Glicopéptidos: '#f1f8e9',
      Sulfonamidas: '#e8eaf6',
      Oxazolidinonas: '#fbe9e7',
      Otros: '#eceff1',
    };

    const bgColor = familyColors[data.value] || '#f5f5f5';

    return (
      <span className='family-badge' style={{ backgroundColor: bgColor }}>
        {data.value}
      </span>
    );
  };

  return (
    <div className='microbiology-catalog-container'>
      <DataGrid ref={dataGridRef} dataSource={dataSource} height={'100%'} width={'100%'} showBorders={true} showRowLines={true} showColumnLines={false} rowAlternationEnabled={true} onEditorPreparing={onEditorPreparing} columnAutoWidth={false}>
        <Scrolling columnRenderingMode='standard' mode='standard' useNative={true} />
        <SearchPanel visible={true} width={300} placeholder='Buscar antibiótico...' />
        <Paging enabled={true} defaultPageSize={25} />
        <Pager visible={true} allowedPageSizes={[25, 50, 100, 200, 'all']} displayMode='full' showPageSizeSelector={true} showInfo={true} showNavigationButtons={true} />

        <Editing mode='popup' allowAdding={true} allowUpdating={true} allowDeleting={true} useIcons={true}>
          <Popup title='Antibiótico' showTitle={true} width={700} height={550}>
            <Form labelLocation='top' colCount={2}>
              <ItemF dataField='Abc_Nombre' />
              <ItemF dataField='Abc_Codigo' />
              <ItemF dataField='Abc_FamiliaAntibiotico' />
              <ItemF dataField='Abc_SubFamilia' />
              <ItemF dataField='Abc_Generacion' />
              <ItemF dataField='Abc_OrdenPresentacion' />
              <ItemF dataField='Abc_Estado' />
            </Form>
          </Popup>
        </Editing>

        <Toolbar>
          <Item name='searchPanel' />
          <Item name='addRowButton' />
          <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Button id='RefrecarResultbc' icon='refresh' text='Refresh' stylingMode='text' onClick={handleRefresh} />
            <Tooltip target={'#RefrecarResultbc'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              Refrescar datos
            </Tooltip>
          </Item>
        </Toolbar>

        <Column dataField='Abc_Id' caption='ID' width={80} alignment='center' dataType='number' />
        <Column dataField='Abc_Nombre' caption='NOMBRE'>
          <RequiredRule message='El nombre es requerido' />
        </Column>

        <Column dataField='Abc_Codigo' caption='CÓDIGO' width={100}>
          <RequiredRule message='El código es requerido' />
        </Column>

        <Column dataField='Abc_FamiliaAntibiotico' caption='FAMILIA' cellRender={familyRender}>
          <Lookup dataSource={familiaOptions} valueExpr='value' displayExpr='text' />
          <RequiredRule message='La familia es requerida' />
        </Column>

        <Column dataField='Abc_SubFamilia' caption='SUBFAMILIA' width={150} />

        <Column dataField='Abc_Generacion' caption='GENERACIÓN' width={130}>
          <Lookup dataSource={generacionOptions} valueExpr='value' displayExpr='text' />
        </Column>

        <Column dataField='Abc_OrdenPresentacion' caption='ORDEN' width={80} alignment='center' dataType='number'>
          <RequiredRule message='El orden es requerido' />
        </Column>

        <Column dataField='Abc_Estado' caption='ESTADO' width={100} alignment='center' />

        <Column type='buttons' width={140} caption='ACCIONES'>
          <GridButton name='edit' />
          <GridButton name='delete' />
          <GridButton icon='copy' hint='Duplicar antibiótico' onClick={handleDuplicate} />
        </Column>
      </DataGrid>
    </div>
  );
};
